import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiClient } from '@/services/api'
import { QuestionDisplay } from '@/components/lessonmaster/QuestionDisplay'
import { useLessonStore } from '@/store/lessonStore'
import { CheckCircle, XCircle, Clock, Users, Target, BarChart3, Play, Loader2, SkipForward, Square, Trophy } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import type { Lesson, LessonActivity, Player } from '@/types'
import { ActivityOutcome, buildOutcomeData } from '@/components/lessonmaster/ActivityOutcome'
import type { ActivityOutcomeData } from '@/components/lessonmaster/ActivityOutcome'

/** Set the active activity.  Slide sync is handled by the store's
 *  _lastPdfActivityId guard (initializes on new PDF activity) and
 *  WebSocket onSlideChanged events (real-time updates from host). */
function syncActivity(activity: LessonActivity | null) {
  useLessonStore.getState().setCurrentActivity(activity)
}

function Leaderboard({ players, lessonName }: { players: Player[]; lessonName: string }) {
  // Sort by total_score descending, then response_count as tiebreaker
  const ranked = [...players]
    .filter(p => !p.is_host)
    .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0) || b.response_count - a.response_count)

  const medalColors = ['text-[var(--color-warning)]', 'text-theme-text', 'text-[var(--color-secondary)]']
  const medalBgs = [
    'bg-[var(--color-warning-subtle)] border-[var(--color-warning)]/30',
    'bg-surface border-theme-border',
    'bg-[var(--color-secondary-subtle)] border-[var(--color-secondary)]/30',
  ]
  const medalAvatars = ['bg-[var(--color-warning)]/60', 'bg-surface-hover', 'bg-[var(--color-secondary)]/60']
  const medalLabels = ['1st', '2nd', '3rd']

  return (
    <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center p-8 overflow-auto">
      <div className="text-center mb-10">
        <h1 className="ds-page-title text-4xl mb-2">Lesson Complete!</h1>
        <p className="text-xl text-theme-text-muted">{lessonName}</p>
      </div>

      <div className="w-full max-w-2xl">
        <h2 className="ds-section-title text-center mb-6" style={{ fontSize: '1.25rem' }}>Leaderboard</h2>

        <div className="space-y-3">
          {ranked.slice(0, 10).map((player, index) => (
            <div
              key={player.id}
              className={`flex items-center gap-4 px-6 py-4 rounded-xl border transition-all ${
                index < 3 ? medalBgs[index] : 'bg-surface border-theme-border'
              }`}
            >
              {/* Rank */}
              <div className={`text-2xl font-bold w-12 text-center shrink-0 ${
                index < 3 ? medalColors[index] : 'text-theme-muted'
              }`}>
                {index < 3 ? medalLabels[index] : `#${index + 1}`}
              </div>

              {/* Name */}
              <div className="flex-1 min-w-0">
                <div className={`font-semibold truncate ${
                  index < 3 ? 'text-lg' : 'text-base'
                }`}>
                  {player.display_name}
                </div>
              </div>

              {/* Score */}
              <div className="text-right shrink-0">
                <div className={`font-bold ${
                  index < 3 ? 'text-xl' : 'text-lg'
                } ${index < 3 ? medalColors[index] : 'text-theme-text'}`}>
                  {player.total_score ?? 0}
                </div>
                <div className="text-xs text-theme-muted">points</div>
              </div>
            </div>
          ))}

          {ranked.length === 0 && (
            <div className="text-center text-theme-muted py-8">
              <p>No student responses recorded.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const MULTI_QUESTION_TYPES = ['packet_analysis', 'cyber_hygiene_quiz', 'coding_quiz']

interface ActivityResultsData {
  activityTitle: string
  activityType: string
  totalPlayers: number
  responseCount: number
  correctCount: number
  incorrectCount: number
  totalQuestions: number
  avgResponseTime: number
  fastestResponse: number | null
  avgScore: number | null
  responses: Array<{
    playerName: string
    score: number | null
    responseTime: number | null
  }>
}

function ActivityResults({ data, onStartNext, startingNext, hasNext, onEndLesson, endingLesson, players }: {
  data: ActivityResultsData
  onStartNext?: () => void
  startingNext?: boolean
  hasNext?: boolean
  onEndLesson?: () => void
  endingLesson?: boolean
  players?: Player[]
}) {
  const responseRate = data.totalPlayers > 0
    ? Math.round((data.responseCount / data.totalPlayers) * 100)
    : 0

  const isMultiQuestion = MULTI_QUESTION_TYPES.includes(data.activityType)
  const hasScoring = data.correctCount + data.incorrectCount > 0
  const correctRate = data.totalQuestions > 0
    ? Math.round((data.correctCount / data.totalQuestions) * 100)
    : 0

  // Cumulative top-3 leaderboard from all player scores so far
  const top3 = [...(players ?? [])]
    .filter(p => !p.is_host)
    .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0) || b.response_count - a.response_count)
    .slice(0, 3)

  const podiumColors = [
    { text: 'text-[var(--color-warning)]', bg: 'bg-[var(--color-warning-subtle)] border-[var(--color-warning)]/40', label: '🥇 1st' },
    { text: 'text-theme-text',             bg: 'bg-surface border-theme-border',                                    label: '🥈 2nd' },
    { text: 'text-[var(--color-secondary)]',bg: 'bg-[var(--color-secondary-subtle)] border-[var(--color-secondary)]/40', label: '🥉 3rd' },
  ]

  return (
    <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center p-8 overflow-auto">
      <div className="text-center mb-6">
        <BarChart3 className="w-10 h-10 text-[var(--color-secondary)] mx-auto mb-3" />
        <h1 className="ds-page-title text-3xl mb-1">Activity Results</h1>
        <p className="text-lg text-theme-text-muted">{data.activityTitle}</p>
      </div>

      {/* Stats grid */}
      <div className={`w-full max-w-4xl grid gap-4 mb-8 ${hasScoring ? 'grid-cols-4' : 'grid-cols-2'}`}>
        <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5 text-center">
          <Users className="w-6 h-6 text-[var(--color-secondary)] mx-auto mb-2" />
          <div className="text-3xl font-bold text-[var(--color-secondary)]">{data.responseCount}</div>
          <div className="text-sm text-theme-text-muted mt-1">Responses</div>
          <div className="text-xs text-theme-muted">{responseRate}% of {data.totalPlayers} students</div>
        </div>

        {hasScoring && (
          <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5 text-center">
            <CheckCircle className="w-6 h-6 text-[var(--color-success)] mx-auto mb-2" />
            <div className="text-3xl font-bold text-[var(--color-success)]">{correctRate}%</div>
            <div className="text-sm text-theme-text-muted mt-1">Correct</div>
            <div className="text-xs text-theme-muted">
              {data.correctCount}/{data.totalQuestions} {isMultiQuestion ? 'answers' : 'responses'}
            </div>
          </div>
        )}

        {hasScoring && (
          <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5 text-center">
            <XCircle className="w-6 h-6 text-[var(--color-danger)] mx-auto mb-2" />
            <div className="text-3xl font-bold text-[var(--color-danger)]">{data.incorrectCount}</div>
            <div className="text-sm text-theme-text-muted mt-1">Incorrect</div>
            <div className="text-xs text-theme-muted">
              out of {data.totalQuestions} {isMultiQuestion ? 'answers' : 'responses'}
            </div>
          </div>
        )}

        <div className="bg-surface-deep border border-[var(--color-border)] rounded-xl p-5 text-center">
          <Clock className="w-6 h-6 text-[var(--color-text-muted)] mx-auto mb-2" />
          <div className="text-3xl font-bold text-theme-text">
            {isMultiQuestion && data.avgScore !== null
              ? `${Math.round(data.avgScore)}%`
              : `${Math.round(data.avgResponseTime)}s`
            }
          </div>
          <div className="text-sm text-theme-text-muted mt-1">
            {isMultiQuestion ? 'Avg Score' : 'Avg Response Time'}
          </div>
          {!isMultiQuestion && data.fastestResponse !== null && (
            <div className="text-xs text-theme-muted">Fastest: {Math.round(data.fastestResponse)}s</div>
          )}
          {isMultiQuestion && (
            <div className="text-xs text-theme-muted">Avg time: {Math.round(data.avgResponseTime)}s</div>
          )}
        </div>
      </div>

      {/* Cumulative top-3 leaderboard */}
      {top3.length > 0 && (
        <div className="w-full max-w-xl mb-8">
          <h3 className="ds-section-title text-center mb-4 flex items-center justify-center gap-2" style={{ fontSize: '1rem' }}>
            <Trophy className="w-5 h-5 text-[var(--color-warning)]" />
            {hasNext ? 'Leaderboard' : 'Final Leaderboard'}
          </h3>
          <div className="space-y-3">
            {top3.map((player, idx) => (
              <div
                key={player.id}
                className={`flex items-center gap-4 px-6 py-4 rounded-xl border ${podiumColors[idx].bg} transition-all`}
              >
                <div className={`text-2xl font-bold w-16 text-center shrink-0 ${podiumColors[idx].text}`}>
                  {podiumColors[idx].label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-lg truncate">{player.display_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-2xl font-bold ${podiumColors[idx].text}`}>
                    {Math.round(player.total_score ?? 0)}
                  </div>
                  <div className="text-xs text-theme-muted">pts total</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start next activity or End lesson button */}
      <div className="flex justify-center">
        {hasNext && onStartNext ? (
          <button
            onClick={onStartNext}
            disabled={startingNext}
            className="ds-btn ds-btn-primary ds-btn-lg hover:-translate-y-0.5 uppercase tracking-wide"
          >
            {startingNext ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Play className="w-5 h-5" />
            )}
            {startingNext ? 'Starting...' : 'Start Next Activity'}
          </button>
        ) : onEndLesson ? (
          <button
            onClick={onEndLesson}
            disabled={endingLesson}
            className="ds-btn ds-btn-danger ds-btn-lg hover:-translate-y-0.5 uppercase tracking-wide"
          >
            {endingLesson ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Square className="w-5 h-5" />
            )}
            {endingLesson ? 'Ending...' : 'End Lesson'}
          </button>
        ) : null}
      </div>
    </div>
  )
}

export function PresentationView() {
  const { runCode } = useParams()
  const navigate = useNavigate()
  const {
    currentLesson,
    currentActivity,
    players,
    setCurrentLesson,
    setPlayers,
    setResponses,
    connectToLesson,
    disconnectFromLesson,
  } = useLessonStore()

  const [error, setError] = useState<string | null>(null)
  const [lessonEnded, setLessonEnded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activityResults, setActivityResults] = useState<ActivityResultsData | null>(null)
  const [outcomeData, setOutcomeData] = useState<ActivityOutcomeData | null>(null)
  const pendingResultsRef = useRef<ActivityResultsData | null>(null)
  const [startingNext, setStartingNext] = useState(false)
  const [endingActivity, setEndingActivity] = useState(false)
  const [startingLesson, setStartingLesson] = useState(false)
  const [endingLesson, setEndingLesson] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const lastActiveActivityRef = useRef<string | null>(null)
  const autoAdvancingRef = useRef(false)

  const getNextPendingActivity = () => {
    if (!currentLesson?.activities) return null
    return currentLesson.activities.find(a => a.status === 'pending') || null
  }

  const SLIDE_TYPES = ['pdf_slideshow', 'google_slides', 'youtube_video']
  const isSlideActivity = currentActivity ? SLIDE_TYPES.includes(currentActivity.activity_type) : false

  // Helper: clear all outcome/results state for a fresh activity cycle
  const clearOutcomeState = () => {
    setActivityResults(null)
    setOutcomeData(null)
    pendingResultsRef.current = null
  }

  const handleEndAndStartNext = async () => {
    // Capture IDs at call time — currentActivity may be cleared by polling before await returns
    const lessonId = currentLesson?.id
    const activityId = currentActivity?.id
      || currentLesson?.activities?.find(a => a.status === 'active')?.id
    if (!lessonId || !activityId) return
    setEndingActivity(true)
    try {
      const res = await apiClient.skipActivity(lessonId, activityId)
      if (res.success) {
        const store = useLessonStore.getState()
        if (res.data) store.updateActivityInLesson(res.data)
        store.setCurrentActivity(null)
        store.setResponses([])

        // Auto-start next pending activity
        const updated = store.currentLesson
        const next = updated?.activities?.find(a => a.status === 'pending')
        if (next && updated?.id) {
          clearOutcomeState()
          const startRes = await apiClient.startActivity(updated.id, next.id)
          if (startRes.success && startRes.data) {
            lastActiveActivityRef.current = next.id
            store.setCurrentActivity(startRes.data)
          }
        }
      }
    } catch {
      // failed
    } finally {
      setEndingActivity(false)
    }
  }

  const handleEndActivityOnly = async () => {
    const lessonId = currentLesson?.id
    const activityId = currentActivity?.id
      || currentLesson?.activities?.find(a => a.status === 'active')?.id
    if (!lessonId || !activityId) return
    setEndingActivity(true)
    try {
      const res = await apiClient.skipActivity(lessonId, activityId)
      if (res.success) {
        const store = useLessonStore.getState()
        if (res.data) store.updateActivityInLesson(res.data)
        store.setCurrentActivity(null)
        store.setResponses([])
      }
    } catch {
      // failed
    } finally {
      setEndingActivity(false)
    }
  }

  // Automatically start the next pending activity without showing the QR/"waiting" screen.
  // Used after slide activities (Google Slides, PDF, YouTube) which produce no results to review,
  // so the lesson flows straight into the next activity.
  const autoStartNextActivity = async () => {
    if (autoAdvancingRef.current) return
    const store = useLessonStore.getState()
    const lesson = store.currentLesson
    if (!lesson?.id || lesson.status === 'completed') return
    const next = lesson.activities?.find(a => a.status === 'pending')
    if (!next) return
    autoAdvancingRef.current = true
    setEndingActivity(true) // reuse the "Starting next activity..." loader to avoid a QR flash
    try {
      clearOutcomeState()
      const res = await apiClient.startActivity(lesson.id, next.id)
      if (res.success && res.data) {
        lastActiveActivityRef.current = next.id
        store.setCurrentActivity(res.data)
      }
    } catch {
      // failed — fall back to the manual "Start Next Activity" button
    } finally {
      setEndingActivity(false)
      autoAdvancingRef.current = false
    }
  }

  const handleStartNext = async () => {
    // Read fresh state from store to avoid stale closure issues
    const store = useLessonStore.getState()
    const lesson = store.currentLesson
    const next = lesson?.activities?.find(a => a.status === 'pending') || null
    if (!next || !lesson?.id) return
    setStartingNext(true)
    try {
      // If there's still an active activity, complete it first — check both store and list
      const storeActive = store.currentActivity?.status === 'active' ? store.currentActivity : null
      const listActive = !storeActive
        ? lesson.activities?.find(a => a.status === 'active' && a.id !== next.id)
        : null
      const activeToComplete = storeActive || listActive
      if (activeToComplete) {
        await apiClient.skipActivity(lesson.id, activeToComplete.id)
        useLessonStore.getState().setCurrentActivity(null)
        useLessonStore.getState().setResponses([])
      }
      const res = await apiClient.startActivity(lesson.id, next.id)
      if (res.success) {
        lastActiveActivityRef.current = next.id
        // Set current activity immediately from API response so the presenter
        // shows the new activity without flashing the QR/join screen
        if (res.data) {
          useLessonStore.getState().setCurrentActivity(res.data)
        }
        clearOutcomeState()
        setStartingNext(false)
      } else {
        setStartingNext(false)
      }
    } catch {
      setStartingNext(false)
    }
  }

  const handleStartLesson = async () => {
    if (!currentLesson?.id) return
    setStartingLesson(true)
    clearOutcomeState()
    try {
      await apiClient.startLesson(currentLesson.id)
      // After starting the lesson, start the first activity automatically
      const first = getNextPendingActivity()
      if (first) {
        const res = await apiClient.startActivity(currentLesson.id, first.id)
        if (res.success) {
          lastActiveActivityRef.current = first.id
        }
      }
    } catch {
      setStartingLesson(false)
    }
  }

  const handleEndLesson = async () => {
    if (!currentLesson?.id) return
    setEndingLesson(true)
    try {
      await apiClient.completeLesson(currentLesson.id)
      // Clear results so the leaderboard shows
      setActivityResults(null)
      setOutcomeData(null)
      pendingResultsRef.current = null
    } catch {
      setEndingLesson(false)
    }
  }

  // Load lesson on mount
  useEffect(() => {
    if (!runCode) return

    const loadLesson = async () => {
      try {
        const infoResponse = await apiClient.getRunInfo(runCode)
        if (!infoResponse.success) {
          setError(infoResponse.message || 'Failed to load lesson')
          return
        }

        if (!infoResponse.data.lesson) {
          setLessonEnded(true)
          return
        }
        const lessonId = infoResponse.data.lesson.id
        const lessonResponse = await apiClient.getLesson(lessonId)
        if (!lessonResponse.success) {
          setError(lessonResponse.message || 'Failed to load lesson')
          return
        }

        const lesson: Lesson = lessonResponse.data.lesson || lessonResponse.data
        setCurrentLesson(lesson)

        if (lesson.players) setPlayers(lesson.players)

        if (lesson.status === 'live' && lesson.activities) {
          const active = lesson.activities.find(a => a.status === 'active')
          if (active) {
            syncActivity(active)
          } else {
            // No active activity — check if the last completed non-slide activity
            // should show the outcome screen (handles page refresh)
            const completedActivities = lesson.activities
              .filter((a: any) => (a.status === 'completed' || a.status === 'skipped') && !SLIDE_TYPES.includes(a.activity_type))
            const lastCompleted = completedActivities[completedActivities.length - 1]
            if (lastCompleted) {
              const nonHostPlayers = (lesson.players || []).filter((p: any) => !p.is_host)
              fetchActivityResults(lesson.id, lastCompleted, nonHostPlayers.length)
            }
          }
        }

        // Connect to WebSocket for real-time slide changes and activity events
        connectToLesson(lesson.id, 'host')
      } catch (err: any) {
        setError(err.message || 'Failed to load lesson')
      } finally {
        setLoading(false)
      }
    }

    loadLesson()

    return () => {
      disconnectFromLesson()
    }
  }, [runCode])

  // Fetch results for a completed activity — shows outcome screen first, then analytics
  const fetchActivityResults = async (lessonId: string, activity: any, playerCount: number) => {
    try {
      const res = await apiClient.getResponses(lessonId, activity.id)
      if (res.success && res.data) {
        const responses = Array.isArray(res.data) ? res.data : (res.data as any).responses || []
        const isMultiQ = MULTI_QUESTION_TYPES.includes(activity.activity_type)
        let correctCount = 0
        let incorrectCount = 0
        let totalQuestions = 0
        let totalTime = 0
        let timedCount = 0
        let fastest: number | null = null
        let totalScore = 0
        let scoredCount = 0

        const mapped = responses.map((r: any) => {
          const score = r.score ?? null
          const responseTime = r.response_time != null ? Math.max(0, r.response_time) : null
          const content = r.content || {}

          if (isMultiQ && Array.isArray(content.answers)) {
            const playerTotal = content.answers.length
            let playerCorrect = 0
            for (const ans of content.answers) {
              totalQuestions++
              if (ans.correct) { correctCount++; playerCorrect++ }
              else incorrectCount++
            }
            // Compute per-player percentage for averaging
            if (playerTotal > 0) {
              totalScore += (playerCorrect / playerTotal) * 100
              scoredCount++
            }
          } else {
            totalQuestions++
            if (score !== null) {
              if (score > 0) correctCount++
              else incorrectCount++
              totalScore += score
              scoredCount++
            }
          }
          if (responseTime !== null) {
            totalTime += responseTime
            timedCount++
            if (fastest === null || responseTime < fastest) fastest = responseTime
          }
          // For multi-question, use percentage as the display score
          const displayScore = isMultiQ && Array.isArray(content.answers) && content.answers.length > 0
            ? Math.round((content.answers.filter((a: any) => a.correct).length / content.answers.length) * 100)
            : score

          return {
            playerName: r.player?.display_name || 'Unknown',
            score: displayScore,
            responseTime,
          }
        })

        const resultsData: ActivityResultsData = {
          activityTitle: activity.title,
          activityType: activity.activity_type,
          totalPlayers: playerCount,
          responseCount: responses.length,
          correctCount,
          incorrectCount,
          totalQuestions,
          avgResponseTime: timedCount > 0 ? totalTime / timedCount : 0,
          fastestResponse: fastest,
          avgScore: scoredCount > 0 ? totalScore / scoredCount : null,
          responses: mapped,
        }

        // Clear any stale results from a previous activity, then show outcome
        setActivityResults(null)
        const outcome = buildOutcomeData(activity, responses, playerCount)
        pendingResultsRef.current = resultsData
        setOutcomeData(outcome)
      }
    } catch {
      // Silently ignore
    }
  }

  const handleShowResults = () => {
    setActivityResults(pendingResultsRef.current)
    pendingResultsRef.current = null
    setOutcomeData(null)
  }

  // When WebSocket clears currentActivity (activity completed), immediately fetch outcome
  // instead of waiting for the next poll. This prevents the outcome screen from being
  // skipped for fast activities like phishing quiz that can auto-complete before the poll.
  const prevActivityRef = useRef<LessonActivity | null>(null)
  useEffect(() => {
    const wasActive = prevActivityRef.current
    prevActivityRef.current = currentActivity

    // Activity just went from active → null (completed via WebSocket)
    if (wasActive && !currentActivity && lastActiveActivityRef.current) {
      // Use the previously active activity object directly — don't look it up
      // in the store because its status may not have been updated yet.
      lastActiveActivityRef.current = null
      if (!SLIDE_TYPES.includes(wasActive.activity_type)) {
        const lesson = useLessonStore.getState().currentLesson
        const nonHostPlayers = (lesson?.players || []).filter((p: any) => !p.is_host)
        fetchActivityResults(lesson?.id || '', wasActive, nonHostPlayers.length)
      } else if (!endingActivity && !startingNext) {
        // Slides produce no results to review — skip the QR/"waiting" screen
        // and flow straight into the next activity.
        autoStartNextActivity()
      }
    }
  }, [currentActivity])

  // Fallback: if the lesson has an active activity but currentActivity is null, sync it
  useEffect(() => {
    if (!currentActivity && currentLesson?.status === 'live' && currentLesson.activities) {
      const active = currentLesson.activities.find(a => a.status === 'active')
      if (active) {
        syncActivity(active)
        lastActiveActivityRef.current = active.id
      }
    }
  }, [currentActivity, currentLesson?.activities, currentLesson?.status])

  // Poll for updates every 5 seconds
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      const lesson = useLessonStore.getState().currentLesson
      if (!lesson?.id) return

      try {
        const response = await apiClient.getLesson(lesson.id)
        if (response.success && response.data) {
          const updated = response.data.lesson || response.data
          setCurrentLesson(updated)
          if (updated.players) setPlayers(updated.players)

          if (updated.activities) {
            const active = updated.activities.find((a: any) => a.status === 'active')

            if (active) {
              // New active activity — always clear old results/outcome
              if (lastActiveActivityRef.current !== active.id) {
                clearOutcomeState()
                setStartingNext(false)
                setStartingLesson(false)
              }
              lastActiveActivityRef.current = active.id
              syncActivity(active)

              // Fetch responses so QuestionDisplay can determine shouldReveal
              try {
                const respRes = await apiClient.getResponses(lesson.id, active.id)
                if (respRes.success && respRes.data) {
                  setResponses(respRes.data)
                }
              } catch { /* best-effort */ }
            } else {
              // No active activity — check if the last tracked one just completed/skipped
              const lastId = lastActiveActivityRef.current
              if (lastId) {
                const finished = updated.activities.find(
                  (a: any) => a.id === lastId && (a.status === 'completed' || a.status === 'skipped')
                )
                if (finished && !SLIDE_TYPES.includes(finished.activity_type)) {
                  // Only fetch if we don't already have outcome data for this activity
                  if (!outcomeData && !activityResults) {
                    const nonHostPlayers = (updated.players || []).filter((p: any) => !p.is_host)
                    fetchActivityResults(lesson.id, finished, nonHostPlayers.length)
                  }
                } else if (finished) {
                  // Slide finished — skip the QR/"waiting" screen and start the next activity.
                  lastActiveActivityRef.current = null
                  autoStartNextActivity()
                }
                lastActiveActivityRef.current = null
              }
              syncActivity(null)
            }
          }
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 5000) // Poll every 5 seconds (stay within rate limits)

    return () => clearInterval(pollInterval)
  }, [])

  // Local countdown timer for the active activity
  useEffect(() => {
    if (!currentActivity || currentActivity.status !== 'active' || !currentActivity.started_at || !currentActivity.time_limit_seconds) {
      setTimeRemaining(null)
      return
    }

    const calcRemaining = () => {
      const startedMs = new Date(currentActivity.started_at!).getTime()
      const endMs = startedMs + currentActivity.time_limit_seconds! * 1000
      return Math.max(0, Math.ceil((endMs - Date.now()) / 1000))
    }

    setTimeRemaining(calcRemaining())
    const interval = setInterval(() => {
      const remaining = calcRemaining()
      setTimeRemaining(remaining)
      if (remaining <= 0) clearInterval(interval)
    }, 1000)
    return () => clearInterval(interval)
  }, [currentActivity?.id, currentActivity?.status, currentActivity?.started_at, currentActivity?.time_limit_seconds])

  if (loading) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full mx-auto mb-4" />
          <p>Loading presentation...</p>
        </div>
      </div>
    )
  }

  if (lessonEnded) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-[var(--color-success)] mx-auto mb-4" />
          <h1 className="ds-page-title text-3xl mb-2">Lesson Ended</h1>
          <p className="text-theme-text-muted mb-8">This lesson has ended. Thanks for participating!</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="ds-btn ds-btn-primary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <p className="text-[var(--color-danger)] text-lg mb-2">Error</p>
          <p className="text-theme-text-muted">{error}</p>
        </div>
      </div>
    )
  }

  const joinUrl = runCode ? `${window.location.origin}/join/${runCode}` : ''
  const isLessonRunning = currentLesson?.status === 'live' || currentLesson?.status === 'running'
  const studentCount = players.filter(p => !p.is_host).length

  // Show a loading screen when the teacher is actively transitioning to the next activity —
  // prevents the outcome/QR screen from flashing during the skip→start gap.
  if (!currentActivity && endingActivity) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[var(--color-primary)] mb-4" />
        <p className="text-theme-text-muted text-lg">Starting next activity...</p>
      </div>
    )
  }

  // Show activity outcome screen (bar chart + correct answer) before detailed results
  // This takes priority over the leaderboard so the last activity's outcome is visible.
  if (!currentActivity && outcomeData && !activityResults) {
    return (
      <ActivityOutcome
        data={outcomeData}
        onShowResults={handleShowResults}
      />
    )
  }

  // Show activity results when an activity just completed and no new one is active
  if (!currentActivity && activityResults) {
    const hasNext = !!getNextPendingActivity()
    return (
      <ActivityResults
        data={activityResults}
        onStartNext={hasNext ? handleStartNext : undefined}
        startingNext={startingNext}
        hasNext={hasNext}
        onEndLesson={!hasNext ? handleEndLesson : undefined}
        endingLesson={endingLesson}
        players={players}
      />
    )
  }

  // Show leaderboard when lesson is completed
  if (currentLesson?.status === 'completed') {
    return <Leaderboard players={players} lessonName={currentLesson.name} />
  }

  // Show loader while lesson is ending (prevents QR code flash)
  if (endingLesson) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[var(--color-primary)] mb-4" />
        <p className="text-theme-text-muted text-lg">Ending lesson...</p>
      </div>
    )
  }

  // Before lesson starts or when lesson is running but no activity is active — show join screen
  if (!isLessonRunning || (!currentActivity && !activityResults)) {
    return (
      <div className="h-screen bg-theme-bg text-theme-text flex flex-col items-center justify-center p-4 overflow-hidden">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="ds-page-title text-3xl mb-1">{currentLesson?.name}</h1>
          {!isLessonRunning && (
            <p className="text-lg text-theme-text-muted mb-6">Waiting to start...</p>
          )}
          {isLessonRunning && (
            <p className="text-lg text-theme-text-muted mb-6">Waiting for next activity...</p>
          )}

          <div className="bg-chrome-bg rounded-2xl border border-theme-border px-8 py-6 mb-5">
            <p className="text-theme-text-muted text-base mb-2">Join this lesson</p>
            <p className="text-theme-muted text-sm mb-4">
              Go to <span className="font-mono text-theme-text">{window.location.origin}/join</span> and enter:
            </p>
            <div className="text-6xl font-mono font-bold text-[var(--color-primary)] tracking-[0.3em] mb-5">
              {runCode}
            </div>
            <div className="flex justify-center">
              <div className="bg-white p-3 rounded-xl">
                <QRCodeSVG value={joinUrl} size={160} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
            </div>
            <p className="text-theme-muted text-xs mt-3">or scan the QR code above</p>
          </div>

          <div className="flex items-center justify-center gap-2 text-theme-text-muted mb-5">
            <Users className="w-5 h-5" />
            <span className="text-lg">
              <span className="text-theme-text font-semibold">{studentCount}</span> student{studentCount !== 1 ? 's' : ''} joined
            </span>
            <div className="w-2 h-2 bg-[var(--color-success)] rounded-full animate-pulse ml-2" />
          </div>

          {/* Start lesson / Start next activity button */}
          {!isLessonRunning && currentLesson?.status === 'draft' && (
            <button
              onClick={handleStartLesson}
              disabled={startingLesson || !currentLesson?.activities?.length}
              className="ds-btn ds-btn-primary ds-btn-lg hover:-translate-y-0.5 uppercase tracking-wide mx-auto"
            >
              {startingLesson ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Play className="w-6 h-6" />
              )}
              {startingLesson ? 'Starting...' : 'Start Lesson'}
            </button>
          )}
          {isLessonRunning && !currentActivity && getNextPendingActivity() && (
            <button
              onClick={handleStartNext}
              disabled={startingNext}
              className="ds-btn ds-btn-primary ds-btn-lg hover:-translate-y-0.5 uppercase tracking-wide mx-auto"
            >
              {startingNext ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <Play className="w-6 h-6" />
              )}
              {startingNext ? 'Starting...' : 'Start Next Activity'}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-theme-bg text-theme-text flex flex-col overflow-hidden">
      {/* Minimal header */}
      <div className="flex items-center justify-between px-6 py-3 bg-chrome-bg border-b border-theme-border shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-lg font-semibold truncate text-[var(--color-primary)]">{currentLesson?.name}</span>
          {currentActivity && (
            <span className="text-sm text-theme-text-muted truncate">
              {currentActivity.activity_type === 'mcq' ? 'Multiple Choice' : currentActivity.activity_type === 'python_repl' ? 'Python Coding' : currentActivity.activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Timer */}
          {timeRemaining !== null && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-lg font-bold ${
              timeRemaining <= 10 ? 'bg-[var(--color-danger-subtle)] text-[var(--color-danger)] animate-pulse' :
              timeRemaining <= 30 ? 'bg-[var(--color-warning-subtle)] text-[var(--color-warning)]' :
              'bg-[var(--color-primary)]/20 text-[var(--color-primary)]'
            }`}>
              <Clock className="w-4 h-4" />
              {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
            </div>
          )}
          {/* End Activity button — shown for timed activities and slide activities */}
          {(timeRemaining !== null || isSlideActivity) && (
            getNextPendingActivity() ? (
              <button
                onClick={handleEndAndStartNext}
                disabled={endingActivity}
                className="ds-btn ds-btn-primary ds-btn-sm"
              >
                {endingActivity ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <SkipForward className="w-4 h-4" />
                )}
                {endingActivity ? 'Starting...' : 'Next Activity'}
              </button>
            ) : (
              <button
                onClick={handleEndActivityOnly}
                disabled={endingActivity}
                className="ds-btn ds-btn-danger ds-btn-sm"
              >
                {endingActivity ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {endingActivity ? 'Ending...' : 'End Activity'}
              </button>
            )
          )}
          <span className="text-sm text-theme-text-muted">
            Join: <span className="font-mono text-[var(--color-primary)]">{runCode}</span>
          </span>
          <button
            onClick={() => setShowQRModal(true)}
            className="bg-white p-1 rounded cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
            title="Enlarge QR code"
          >
            <QRCodeSVG value={joinUrl} size={36} bgColor="#ffffff" fgColor="#000000" level="M" />
          </button>
        </div>
      </div>

      {/* Full-screen question display */}
      <div className="flex-1 flex flex-col">
        <QuestionDisplay
          showAdvanceControls={timeRemaining !== null || isSlideActivity}
          hasNextActivity={!!getNextPendingActivity()}
          advancing={endingActivity}
          onNextActivity={handleEndAndStartNext}
          onEndActivity={handleEndActivityOnly}
        />
      </div>

      {/* QR Code Modal */}
      {showQRModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8"
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="bg-chrome-bg rounded-2xl border border-theme-border px-8 py-6 max-w-lg w-full text-center max-h-[calc(100vh-4rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-theme-text-muted text-base mb-2">Join this lesson</p>
            <p className="text-theme-muted text-sm mb-4">
              Go to <span className="font-mono text-theme-text">{window.location.origin}/join</span> and enter:
            </p>
            <div className="text-5xl font-mono font-bold text-[var(--color-primary)] tracking-[0.3em] mb-5">
              {runCode}
            </div>
            <div className="flex justify-center mb-3">
              <div className="bg-white p-3 rounded-xl">
                <QRCodeSVG value={joinUrl} size={160} bgColor="#ffffff" fgColor="#000000" level="M" />
              </div>
            </div>
            <p className="text-theme-muted text-xs mb-4">or scan the QR code above</p>
            <button
              onClick={() => setShowQRModal(false)}
              className="ds-btn ds-btn-secondary ds-btn-sm"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
